import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Modal,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  BookOutlined,
  ReloadOutlined,
  SaveOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  PROMPT_CATEGORIES,
  PROMPT_DEFAULTS,
  PROMPT_META,
  getOverriddenKeys,
  getPromptBlock,
  isPromptBlockModified,
  resetAllPromptBlocks,
  setPromptBlock,
} from "../services/promptStore";

const { Text } = Typography;

// ─── Single block editor ──────────────────────────────────────────────────────

const BlockEditor = ({ blockKey, onAnyChange }) => {
  const [draft, setDraft] = useState(() => getPromptBlock(blockKey));
  const meta = PROMPT_META[blockKey] || {};
  const defaultVal = PROMPT_DEFAULTS[blockKey] ?? "";
  const isModified = draft !== defaultVal;

  const handleSave = () => {
    setPromptBlock(blockKey, draft);
    onAnyChange?.();
    message.success(`"${meta.label}" saved.`);
  };

  const handleReset = () => {
    setDraft(defaultVal);
    setPromptBlock(blockKey, defaultVal);
    onAnyChange?.();
  };

  return (
    <div style={{ marginBottom: 28 }}>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 6, display: "flex" }}>
        <Space size={6}>
          <Text strong style={{ fontSize: 13 }}>{meta.label || blockKey}</Text>
          {isModified && <Tag color="orange" icon={<WarningOutlined />} style={{ fontSize: 11 }}>Modified</Tag>}
        </Space>
        <Space size={6}>
          <Tooltip title="Reset to default">
            <Button size="small" icon={<ReloadOutlined />} onClick={handleReset} disabled={!isModified}>
              Reset
            </Button>
          </Tooltip>
          <Button size="small" type="primary" icon={<SaveOutlined />} onClick={handleSave} disabled={!isModified}>
            Save
          </Button>
        </Space>
      </Space>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        style={{
          width: "100%",
          minHeight: 180,
          fontFamily: "monospace",
          fontSize: 12,
          lineHeight: 1.5,
          padding: "8px 10px",
          borderRadius: 6,
          border: isModified ? "1.5px solid #fa8c16" : "1px solid #d9d9d9",
          background: isModified ? "#fffbe6" : "#fafafa",
          resize: "vertical",
          boxSizing: "border-box",
          outline: "none",
        }}
      />
      <Text type="secondary" style={{ fontSize: 11 }}>
        {draft.length.toLocaleString()} chars
        {isModified ? " · Modified — click Save to apply" : " · Default"}
      </Text>
    </div>
  );
};

// ─── Main modal ───────────────────────────────────────────────────────────────

const PromptLibraryModal = ({ open, onClose }) => {
  const [modifiedCount, setModifiedCount] = useState(0);

  const refreshCount = useCallback(() => {
    setModifiedCount(getOverriddenKeys().length);
  }, []);

  useEffect(() => {
    if (open) refreshCount();
  }, [open, refreshCount]);

  const handleResetAll = () => {
    resetAllPromptBlocks();
    refreshCount();
    message.success("All prompt blocks reset to defaults.");
  };

  const tabs = PROMPT_CATEGORIES.map((cat) => {
    const keys = Object.keys(PROMPT_META).filter((k) => PROMPT_META[k].category === cat);
    const anyModified = keys.some(isPromptBlockModified);
    return {
      key: cat,
      label: (
        <Space size={4}>
          {cat}
          {anyModified && <Badge dot color="orange" />}
        </Space>
      ),
      children: (
        <div style={{ paddingTop: 8 }}>
          {keys.map((k) => (
            <BlockEditor key={k} blockKey={k} onAnyChange={refreshCount} />
          ))}
        </div>
      ),
    };
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <Space>
          <BookOutlined />
          <span>Prompt Library</span>
          {modifiedCount > 0 && (
            <Tag color="orange" icon={<WarningOutlined />}>
              {modifiedCount} modified
            </Tag>
          )}
        </Space>
      }
      width={860}
      footer={
        <Space style={{ width: "100%", justifyContent: "space-between", display: "flex" }}>
          <Tooltip title="Revert every block back to its built-in default">
            <Button danger icon={<ReloadOutlined />} onClick={handleResetAll} disabled={modifiedCount === 0}>
              Reset All to Defaults
            </Button>
          </Tooltip>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
      styles={{ body: { maxHeight: "72vh", overflowY: "auto", padding: "8px 24px 0" } }}
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 12 }}>
        These are the static text blocks injected into every AI prompt. Edit them to tune the model's behaviour — changes persist in your browser and apply immediately to all future generations.
      </Text>
      <Tabs items={tabs} size="small" />
    </Modal>
  );
};

/**
 * Small trigger button for the Prompt Library.
 */
export const PromptLibraryButton = ({ onClick, modifiedCount = 0 }) => (
  <Badge count={modifiedCount} color="orange" size="small" offset={[-2, 2]}>
    <Tooltip title={modifiedCount > 0 ? `Prompt Library — ${modifiedCount} block(s) modified` : "Edit AI prompt text blocks"}>
      <Button icon={<BookOutlined />} onClick={onClick} size="small">
        Prompt Library
      </Button>
    </Tooltip>
  </Badge>
);

export default PromptLibraryModal;
